/**
 * useCapturePrompt — the in-call poll for officer-initiated guided-capture
 * prompts (S13-9 slice 1; the SessionLanding PREPARING_POLL_MS idiom, on a
 * 5s cadence because a live officer is waiting on the other side).
 *
 * SAFE-POINT rules encoded here (S4-7 defer-not-interrupt — the droom side):
 *  • The poll runs ONLY while the caller says the call is at a safe point
 *    (`enabled`): gates resolved, transport connected/resumed — never during
 *    consent, permission recovery, or a reconnect.
 *  • `paused` suspends the cadence while a capture ceremony is in flight
 *    (mid-shot / mid-upload) — a new server state never yanks the UI mid-step.
 *  • The poll also detects WITHDRAWAL: if the served prompt disappears or
 *    changes id while the overlay is open, the overlay closes calmly (the
 *    officer cancelled / it expired) — the customer is never left completing
 *    a dead request.
 *
 * Poll failures are silent no-ops (the call must never degrade because a
 * side-channel read hiccuped); the next tick retries. Mock mode never yields
 * a prompt (the API layer is honest about having no channel).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPendingPrompt, type CapturePrompt } from '@/api/prompts';

const PROMPT_POLL_MS = 5_000;

export interface CapturePromptState {
  /** The current in-flight prompt at a safe point, or null. */
  readonly prompt: CapturePrompt | null;
  /** Clear the local prompt after a terminal outcome (completed / withdrawn). */
  readonly clear: () => void;
}

export function useCapturePrompt(
  sessionId: string,
  enabled: boolean,
  paused: boolean,
): CapturePromptState {
  const [prompt, setPrompt] = useState<CapturePrompt | null>(null);
  const promptRef = useRef<CapturePrompt | null>(null);
  promptRef.current = prompt;

  useEffect(() => {
    if (!enabled || !sessionId || paused) return undefined;
    let active = true;
    let timer: number | undefined;

    const tick = () => {
      fetchPendingPrompt(sessionId)
        .then((served) => {
          if (!active) return;
          const current = promptRef.current;
          if (!served) {
            // Nothing pending server-side: withdraw any locally-shown prompt
            // (officer cancel / expiry) — never leave a dead request on screen.
            if (current) setPrompt(null);
          } else if (!current || current.id !== served.id || current.state !== served.state) {
            setPrompt(served);
          }
        })
        .catch(() => {
          // Silent: the call never degrades over a side-channel read; next tick retries.
        })
        .finally(() => {
          if (active) timer = window.setTimeout(tick, PROMPT_POLL_MS);
        });
    };

    tick();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [sessionId, enabled, paused]);

  const clear = useCallback(() => setPrompt(null), []);

  return { prompt, clear };
}
